import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import IconButton from '@mui/material/IconButton'
import ImageWithSkeleton from '../../../../../../components/ImageWithSkeleton'
import { styled } from '@mui/material/styles'
import StarIcon from '@mui/icons-material/Star'
import StarHalfIcon from '@mui/icons-material/StarHalf'
import StarOutlineIcon from '@mui/icons-material/StarOutlineOutlined'
import { useTranslation } from 'react-i18next'

const HotelCard = styled(Card)(({ }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column'
}))

const StarRating = ({ rating }) => {
  // If rating is a string, just display it
  if (typeof rating === 'string') {
    return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{rating}</Box>
  }

  const stars = []
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 !== 0

  for (let i = 0; i < fullStars; i++) {
    stars.push(<StarIcon key={`full-${i}`} sx={{ color: '#FFD700', fontSize: '16px' }} />)
  }

  if (hasHalfStar) {
    stars.push(<StarHalfIcon key='half' sx={{ color: '#FFD700', fontSize: '16px' }} />)
  }

  const emptyStars = 5 - stars.length
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<StarOutlineIcon key={`empty-${i}`} sx={{ color: '#FFD700', fontSize: '16px', opacity: 0.3 }} />)
  }

  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{stars}</Box>
}

const ActionButton = ({ href, title, icon, color }) => (
  <IconButton
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    title={title}
    sx={{
      backgroundColor: color,
      '&:hover': { backgroundColor: color, opacity: 0.8 },
      width: 40,
      height: 40
    }}
  >
    {icon}
  </IconButton>
)

export default function TravelGuide ({ race }) {
  const { t } = useTranslation('raceGuide')
  const { accommodation } = race

  if (!accommodation) {
    return <Box>{t('travelGuide.noAccommodation')}</Box>
  }

  const { title, subtitle, chapters } = accommodation
  const hotelsChapter = chapters.find((chapter) => chapter.hotels)
  const hotels = hotelsChapter?.hotels || []

  return (
    <Box>
      {/* Main Title */}

      {hotelsChapter?.image &&
        <Box sx={{ display: 'flex', my: 3 }}>
          <ImageWithSkeleton
            src={hotelsChapter?.image}
            // maxWidth='96px'
            oneSize
            imageSx={{ borderRadius: 2 }}
          />
          </Box>
      }
      <Typography
        variant='h5'
        component='h2'
        gutterBottom
        sx={{
          fontWeight: 'bold'
        }}
      >
        {title}
      </Typography>

      {/* Subtitle */}
      <Typography
        gutterBottom
        sx={{
          color: 'text.secondary',
          fontStyle: 'italic'
        }}
      >
        {subtitle}
      </Typography>

      {/* Hotels Section */}
      {hotelsChapter && (
        <Box sx={{ mb: 6 }}>
          <Typography
            variant='h6'
            component='h3'
            sx={{
              fontWeight: 'bold',
              mt: 2
            }}
          >
            {hotelsChapter.title}
          </Typography>

          <Typography
            variant='body1'
            sx={{ mb: 4, color: 'text.secondary' }}
          >
            {hotelsChapter.description}
          </Typography>

          {/* Hotels Grid */}
          <Grid container spacing={1} sx={{ mb: 4 }}>
            {hotels.map((hotel, index) => (
              <Grid size={{ xs: 6, md: 4, lg: 3 }} key={index}>
                <HotelCard>
                  <CardContent sx={{ flexGrow: 1, py: '8px !important' }}>
                    <Typography
                      sx={{
                        mb: 1,
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      {hotel.hotel}
                    </Typography>

                    {/* Star Rating */}
                    {hotel.stars && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <StarRating rating={hotel.stars} />
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 200, margin: 'auto' }}>
                      {hotel.official && (
                        <ActionButton
                          href={`https://${hotel.official}`}
                          title={t('travelGuide.officialWebsite')}
                          icon={<LanguageOutlinedIcon sx={{ color: '#1976D2' }} />}
                          color='rgba(25, 118, 210, 0.1)'
                        />
                      )}

                      {hotel.map && (
                        <ActionButton
                          href={`https://maps.app.goo.gl/${hotel.map}`}
                          title={t('travelGuide.viewOnMap')}
                          icon={<RoomOutlinedIcon sx={{ color: '#4CAF50' }} />}
                          color='rgba(76, 175, 80, 0.1)'
                        />
                      )}

                      {hotel.booking && (
                        <ActionButton
                          href={`https://www.booking.com/hotel/ae/${hotel.booking}`}
                          title={t('travelGuide.bookNow')}
                          icon={<EventAvailableOutlinedIcon sx={{ color: '#FF5722' }} />}
                          color='rgba(255, 87, 34, 0.1)'
                        />
                      )}
                    </Box>
                  </CardContent>
                </HotelCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Other Chapters */}
      {chapters
        .filter((chapter) => !chapter.hotels)
        .map((chapter, index) => (
          <Box key={index} sx={{ mb: 4 }}>
            <Typography
              variant='h6'
              component='h3'
              sx={{ fontWeight: 'bold' }}
            >
              {chapter.title}
            </Typography>

            <Typography
              variant='body1'
              paragraph
              sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
            >
              {chapter.description}
            </Typography>

            {chapter.url && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant='outlined'
                  href={chapter.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  size='large'
                >
                  {chapter.urlName || t('travelGuide.learnMore')}
                </Button>
              </Box>
            )}
          </Box>
        ))}
    </Box>
  )
}
